import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ChevronDown } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';

import { CATALOG_MENU_ID, catalogMenuStore } from '@features/catalog-menu';
import { ThemeSwitcher } from '@features/theme-switcher';
import { Logo, LOGO_HOME_LABEL } from '@shared/ui/Logo';

import styles from './AppHeader.module.css';

const MOBILE_MENU_ID = 'mobile-menu';

const NAV_LINKS = [
  { path: '/projects', label: 'Проекты' },
  { path: '/contacts', label: 'Контакты' },
] as const satisfies { path: string; label: string }[];

export const AppHeader: FC = observer(() => {
  const location = useLocation();

  // Мобильное меню нужно только шапке — локальное состояние, а не стор
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const resetHomeScrollRef = useRef(false);

  useLayoutEffect(() => {
    if (!resetHomeScrollRef.current || location.pathname !== '/') return;
    resetHomeScrollRef.current = false;
    // Сбрасываем после перехода, чтобы не менять прокрутку исходной страницы.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.key, location.pathname]);

  // Паттерн «раскрывающаяся навигация», а не модальное окно: Esc закрывает и возвращает фокус на бургер
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMobileMenuOpen(false);
      burgerRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  const isCatalogActive = catalogMenuStore.isOpen;

  const handleCatalogClick = useCallback(() => {
    catalogMenuStore.toggle();
    setMobileMenuOpen(false);
  }, []);

  // Меню и каталог не открыты одновременно: обе панели под шапкой и перекрыли бы друг друга
  const handleBurgerClick = useCallback(() => {
    catalogMenuStore.close();
    setMobileMenuOpen((open) => !open);
  }, []);

  // Переход делает сам Link; по обычному клику только закрываем меню
  const handleNavClick = useCallback(() => {
    catalogMenuStore.close();
    setMobileMenuOpen(false);
  }, []);

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    handleNavClick();
    resetHomeScrollRef.current = true;
  };

  return (
    <>
      <header className={styles.root}>
        <Link
          className={styles.logoBlock}
          to="/"
          onClick={handleLogoClick}
          aria-label={LOGO_HOME_LABEL}
        >
          <Logo />
        </Link>

        <nav className={styles.nav} aria-label="Основная навигация">
          <button
            className={styles.navItem}
            onClick={handleCatalogClick}
            aria-expanded={isCatalogActive}
            aria-controls={CATALOG_MENU_ID}
            data-catalog-trigger="true"
          >
            Каталог <ChevronDown aria-hidden />
          </button>
          {NAV_LINKS.map(({ path, label }) => (
            <Link
              className={styles.navLink}
              key={path}
              to={path}
              aria-current={location.pathname === path ? 'page' : undefined}
              onClick={handleNavClick}
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className={styles.rightSlot}>
          <ThemeSwitcher />
        </div>

        <button
          className={styles.burgerButton}
          ref={burgerRef}
          onClick={handleBurgerClick}
          aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={isMobileMenuOpen}
          aria-controls={MOBILE_MENU_ID}
          // Сюда каталог вернёт фокус по Escape, если открывшая его кнопка спряталась вместе с меню
          data-menu-focus-fallback="true"
        >
          {[0, 1, 2].map((i) => (
            <span className={styles.burgerLine} key={i} />
          ))}
        </button>
      </header>

      <nav
        className={styles.mobileMenu}
        id={MOBILE_MENU_ID}
        aria-label="Меню"
        data-open={isMobileMenuOpen}
      >
        <button
          className={styles.mobileNavItem}
          onClick={handleCatalogClick}
          aria-expanded={isCatalogActive}
          aria-controls={CATALOG_MENU_ID}
          data-catalog-trigger="true"
        >
          Каталог <ChevronDown aria-hidden />
        </button>
        {NAV_LINKS.map(({ path, label }) => (
          <Link className={styles.mobileNavLink} key={path} to={path} onClick={handleNavClick}>
            {label}
          </Link>
        ))}
      </nav>
    </>
  );
});
